import { CubePresenter, FlipIntent, Logger } from 'wdc-cube'

import { WaitingScope } from 'src/components/waiting/waiting.scopes'
import { MainPresenter } from 'src/main/Main.presenter'
import { formatCPFOrCNPJ, formatDateNoTz } from 'src/utils/formatter-utils'
import BorderoKeys from '../bordero.keys'
import borderoService from '../bordero.service'
import type { ShowResultadoDetalhado } from '../bordero.types'
import { DialogoResultadoKeys } from './dialogo-resultado'
import { ResultadoDetalhadoAnaliseKeys } from './resultado_detalhado_analise.keys'
import ResultadoDetalhadoAnaliseScope from './resultado_detalhado_analise.scopes'

const LOG = Logger.get('ResultadoDetalhadoAnalisePresenter')

export class ResultadoDetalhadoAnalisePresenter extends CubePresenter<MainPresenter, ResultadoDetalhadoAnaliseScope> {
    // :: Constructor
    public constructor(app: MainPresenter) {
        super(app, new ResultadoDetalhadoAnaliseScope())
    }

    // :: Instance
    #borderoId?: number
    #resultadoShowOrigem?: string
    #resultadoShowIdOrigem?: number
    #resultadoShowIdSacado?: number
    #resultDetalhadoShow?: ShowResultadoDetalhado

    public override release() {
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(
        intent: FlipIntent,
        initialization: boolean,
        last: boolean
    ): Promise<boolean> {
        const keys = new ResultadoDetalhadoAnaliseKeys(this.app, intent)

        keys.parentSlot(this.scope)

        if (initialization) {
            await this.#initialize()
        }

        if (this.#borderoId != keys.borderoId) {
            this.#borderoId = keys.borderoId
            await this._loadResults()
        }

        this.#resultDetalhadoShow = keys.resultadoDetalhadoShow
        this.#resultadoShowOrigem = keys.resultadoShowOrigem
        this.#resultadoShowIdOrigem = keys.resultadoShowIdOrigem
        this.#resultadoShowIdSacado = keys.resultadoShowIdSacado

        if (last) {
            keys.dialogSlot(null)
            this.#resultDetalhadoShow = undefined
            this.#resultadoShowOrigem = undefined
            this.#resultadoShowIdOrigem = undefined
            this.#resultadoShowIdSacado = undefined
        }

        if (!this.#resultDetalhadoShow && this.#resultadoShowOrigem) {
            await this._loadShowResults()
            keys.resultadoDetalhadoShow = this.#resultDetalhadoShow
        }
        return keys.allow
    }

    public override async publishParameters(intent: FlipIntent): Promise<void> {
        const keys = new ResultadoDetalhadoAnaliseKeys(this.app, intent)

        keys.borderoId = this.#borderoId
        keys.resultadoShowOrigem = this.#resultadoShowOrigem
        keys.resultadoShowIdOrigem = this.#resultadoShowIdOrigem
        keys.resultadoShowIdSacado = this.#resultadoShowIdSacado
        keys.resultadoDetalhadoShow = this.#resultDetalhadoShow

        LOG.debug(`publishParameters ${keys.borderoId}`)
    }

    async #initialize() {
        LOG.debug('initialize')

        this.scope.onClose = this.action(this.handleClose)
        this.scope.onShowResults = this.action(this.handleShowResults)
    }

    private async handleClose() {
        await new BorderoKeys(this.app).flip()
    }

    private async handleShowResults(result: ShowResultadoDetalhado) {
        this.#resultDetalhadoShow = result
        this.#resultadoShowOrigem = result.origem
        this.#resultadoShowIdOrigem = result.idOrigem
        this.#resultadoShowIdSacado = result.idSacado

        const key = new DialogoResultadoKeys(this.app)
        await key.flip()
    }

    private async _loadResults() {
        try {
            this.scope.waiting = new WaitingScope()

            if (!this.#borderoId) throw new Error('Id do borderô deve ser informado')
            this.scope.resultado = await borderoService.fetchResultadoDetalhadoBordero(this.#borderoId)
        } catch (caught) {
            LOG.error('handleFind', caught)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }
    }

    private async _loadShowResults() {
        if (!this.#resultadoShowIdOrigem || !this.#resultadoShowOrigem) {
            this.#resultDetalhadoShow = undefined
            return
        }

        if (this.#resultadoShowOrigem === 'Cedente') {
            this.#resultDetalhadoShow = {
                origem: 'Cedente',
                idOrigem: this.#resultadoShowIdOrigem,
                idSacado: this.#resultadoShowIdSacado,
                documento: formatCPFOrCNPJ(this.scope.resultado.cedente.cnpjCpf),
                nome: this.scope.resultado.cedente.nome,
                resultado: this.scope.resultado.cedente.resultados
            }
        } else if (this.#resultadoShowOrigem === 'Sacado') {
            const sacados = this.scope.resultado.sacados.filter((sac) => Number(sac.id) === this.#resultadoShowIdOrigem)
            if (sacados && sacados.length > 0) {
                const sacado = sacados[0]

                this.#resultDetalhadoShow = {
                    origem: 'Sacado',
                    idOrigem: this.#resultadoShowIdOrigem,
                    idSacado: this.#resultadoShowIdSacado,
                    documento: formatCPFOrCNPJ(sacado.cnpjCpf),
                    nome: sacado.nome,
                    resultado: sacado.resultados
                }
            }
        } else if (this.#resultadoShowOrigem === 'Título') {
            const sacados = this.scope.resultado.sacados.filter((sac) => Number(sac.id) === this.#resultadoShowIdSacado)
            const sacado = sacados && sacados.length > 0 ? sacados[0] : undefined
            const titulos = sacado?.titulos?.filter((titulo) => Number(titulo.id) === this.#resultadoShowIdOrigem)
            if (titulos && titulos.length > 0) {
                const titulo = titulos[0]

                this.#resultDetalhadoShow = {
                    origem: 'Título',
                    idOrigem: this.#resultadoShowIdOrigem,
                    idSacado: this.#resultadoShowIdSacado,

                    nome: titulo.documento,
                    documento: titulo.vencimento ? '' : `Vecimento ${formatDateNoTz(titulo.vencimento)}`,
                    resultado: titulo.resultados
                }
            }
        } else if (this.#resultadoShowOrigem === 'NF-e') {
            const sacados = this.scope.resultado.sacados.filter((sac) => Number(sac.id) === this.#resultadoShowIdSacado)
            const sacado = sacados && sacados.length > 0 ? sacados[0] : undefined
            const notas = sacado?.notas?.filter((nota) => Number(nota.id) === this.#resultadoShowIdOrigem)
            if (notas && notas.length > 0) {
                const nota = notas[0]

                this.#resultDetalhadoShow = {
                    origem: 'NF-e',
                    idOrigem: this.#resultadoShowIdOrigem,
                    idSacado: this.#resultadoShowIdSacado,

                    nome: nota.chave,
                    documento: '',
                    resultado: nota.resultados
                }
            }
        }
    }
}

export default ResultadoDetalhadoAnalisePresenter
